import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { usePlan } from '../context/PlanContext';
import SEO from '../components/SEO';
import { CreditBar } from '../components/CreditDisplay';
import { useBlueprintClaim } from '../hooks/useBlueprintClaim';
import PendingBlueprintBanner from '../components/PendingBlueprintBanner';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useCommand } from '../context/CommandContext';
import RunAgentModal from '../components/RunAgentModal';

import {
  ArrowRight,
  Buildings,
  CaretRight,
  ChartBar,
  CheckCircle,
  Clock,
  Compass,
  FileText,
  GearSix,
  Headphones,
  Lightbulb,
  Lightning,
  Megaphone,
  Microscope,
  Play,
  ShieldCheck,
  Sparkle,
  SquaresFour,
  TrendUp,
  UsersThree,
  Wallet,
  Warning,
  WarningCircle,
  Wrench,
  XCircle,
} from '@phosphor-icons/react';
import { AgentAvatar } from '../components/AgentAvatar';
import { OperationalMetrics } from '../components/OperationalMetrics';
import { ActivityFeed } from '../components/ActivityFeed';
import { AgentStatusIndicator, deriveOperationalStatus } from '../components/AgentStatusIndicator';

const OUTCOME_COLORS = {
  'Increase Sales': { bg: 'rgba(16,185,129,0.10)', text: '#10B981' },
  'Save Time': { bg: 'rgba(59,130,246,0.10)', text: '#3B82F6' },
  'Reduce Admin': { bg: 'rgba(168,85,247,0.10)', text: '#A855F7' },
  'Customer Support': { bg: 'rgba(236,72,153,0.10)', text: '#EC4899' },
  'Marketing': { bg: 'rgba(249,115,22,0.10)', text: '#F97316' },
  'Finance & Admin': { bg: 'rgba(234,179,8,0.10)', text: '#EAB308' },
  'Operations': { bg: 'rgba(99,102,241,0.10)', text: '#6366F1' },
  'Research & Analysis': { bg: 'rgba(20,184,166,0.10)', text: '#14B8A6' },
};

const OUTCOME_ICONS = {
  'Increase Sales': TrendUp,
  'Save Time': Clock,
  'Reduce Admin': FileText,
  'Customer Support': Headphones,
  'Marketing': Megaphone,
  'Finance & Admin': Wallet,
  'Operations': GearSix,
  'Research & Analysis': Microscope,
};

const QUICK_ACTIONS = [
  { icon: Play, title: 'Run Something', description: 'Execute an installed agent', path: '/partner-pro/ai-workforce/agents', color: '#10B981' },
  { icon: Wrench, title: 'Build Something', description: 'Create a custom AI agent', action: 'open_command', color: '#6366F1' },
  { icon: Compass, title: 'Explore', description: 'Browse automations & apps', path: '/automations', color: '#EC4899' },
  { icon: Lightbulb, title: 'Find Ideas', description: 'Discover automation opportunities', path: '/assessments', color: '#F97316' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { plan } = usePlan();
  const { user: authUser } = useAuth();
  const { openCommand } = useCommand();
  const [agents, setAgents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [agentToRun, setAgentToRun] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  const isPro = plan === 'pro' || plan === 'partner_pro' || plan === 'partner-pro';
  const isPartnerPro = plan === 'partner_pro' || plan === 'partner-pro';

  // Blueprint claim
  const blueprintClaim = useBlueprintClaim(authUser, activeOrganization?.organization_id);
  useEffect(() => {
    if (blueprintClaim.claimed && blueprintClaim.processId) {
      toast.success('Your AI Blueprint has been saved!', {
        action: { label: 'View Process', onClick: () => navigate(`/processes/${blueprintClaim.processId}`) }
      });
    }
  }, [blueprintClaim.claimed, blueprintClaim.processId, navigate]);

  useEffect(() => { fetchData(); fetchAutomations(); fetchUserInfo(); fetchPendingApprovals(); }, [activeOrganization]);

  useEffect(() => {
    if (isPro) fetchUsageStats();
    if (isPartnerPro) fetchClientOrgs();
  }, [isPro, isPartnerPro, activeOrganization]);

  const fetchUserInfo = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUserName(data.name?.split(' ')[0] || '');
        if (data.credits) setCredits(data.credits);
      }
    } catch { /* ignore */ }
  };

  const fetchData = async () => {
    if (!activeOrganization?.organization_id) return;
    try {
      setLoading(true);
      const [agentsRes, runsRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/agents`, {}, activeOrganization.organization_id),
        fetchWithOrg(`${BACKEND_URL}/api/agent/all/runs?limit=5&page=1`, {}, activeOrganization.organization_id),
      ]);
      if (agentsRes.ok) { const data = await agentsRes.json(); setAgents((data.agents || data || []).slice(0, 6)); }
      if (runsRes.ok) { const data = await runsRes.json(); setRuns((data.runs || data || []).slice(0, 5)); }
    } catch (e) { console.error('Dashboard fetch error:', e); } finally { setLoading(false); }
  };

  const fetchAutomations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/automations?limit=100`);
      if (!res.ok) return;
      const data = await res.json();
      const shuffled = (data.templates || []).sort(() => 0.5 - Math.random());
      setAutomations(shuffled.slice(0, 6));
    } catch { /* ignore */ }
  };

  const fetchUsageStats = async () => {
    if (!activeOrganization?.organization_id) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/agents`, {}, activeOrganization.organization_id);
      if (res.ok) {
        const data = await res.json();
        const allAgents = data.agents || data || [];
        const deployed = allAgents.filter(a => a.status === 'active').length;
        setUsageStats({ agentsDeployed: deployed, totalAgents: allAgents.length });
      }
    } catch { /* ignore */ }
  };

  const fetchClientOrgs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/organizations`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setClientOrgs((data.organizations || data || []).slice(0, 5));
      }
    } catch { /* ignore */ }
  };

  const fetchPendingApprovals = async () => {
    if (!activeOrganization?.organization_id) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/approvals/pending/count`, {}, activeOrganization.organization_id);
      if (res.ok) {
        const data = await res.json();
        setPendingApprovals(data.pending_count || 0);
      }
    } catch { /* ignore */ }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Derive system health from fetched data
  const degradedAgents = agents.filter(a => {
    const status = deriveOperationalStatus(a);
    return status === 'warning' || status === 'error' || status === 'degraded';
  });
  const failedRuns = runs.filter(r => r.status === 'failed');
  const hasIssues = degradedAgents.length > 0 || failedRuns.length > 0 || pendingApprovals > 0;
  const systemStatus = failedRuns.length > 0 || degradedAgents.length > 1 ? 'issues' : hasIssues ? 'warnings' : 'healthy';

  // Build attention items
  const attentionItems = [];
  if (pendingApprovals > 0) {
    attentionItems.push({
      id: 'approvals',
      icon: ShieldCheck,
      color: '#F59E0B',
      title: `${pendingApprovals} approval${pendingApprovals > 1 ? 's' : ''} pending`,
      action: { label: 'Review', path: '/approval-dashboard' },
    });
  }
  degradedAgents.forEach(agent => {
    attentionItems.push({
      id: `agent-${agent.agent_id || agent.id}`,
      icon: WarningCircle,
      color: '#EF4444',
      title: `${agent.name} needs attention`,
      action: { label: 'Investigate', path: `/unified-agent-builder/${agent.agent_id || agent.id}` },
    });
  });
  failedRuns.forEach(run => {
    attentionItems.push({
      id: `run-${run.run_id || run.id}`,
      icon: XCircle,
      color: '#EF4444',
      title: `${run.agent_name || 'Agent'} run failed`,
      action: { label: 'View', path: '/partner-pro/ai-workforce/run-history' },
    });
  });

  return (
    <>
      <SEO title="Dashboard | Man & Machine" />
      <div className="min-h-screen p-4 md:p-6 lg:p-8 space-y-6" data-testid="unified-dashboard">
        {/* Blueprint Banner */}
        {blueprintClaim.pending && <PendingBlueprintBanner />}

        {/* Welcome Header + Credits */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {userName ? `Welcome back, ${userName}` : 'Welcome back'}
            </h1>
            <p className="text-sm text-[#8b8fa3]">Here's your system at a glance.</p>
          </div>
          {credits && (
            <div className="lg:w-72 shrink-0">
              <CreditBar credits={credits} />
              <p className="text-[10px] text-[#555] mt-1 text-right" data-testid="credits-helper-text">AI actions consume credits.</p>
            </div>
          )}
        </div>

        {/* ═══ SYSTEM STATUS STRIP ═══ */}
        <section data-testid="system-status-strip">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Overall status */}
            <div
              onClick={() => systemStatus !== 'healthy' ? navigate('/run-health') : null}
              className={`rounded-xl border p-3 md:p-4 flex items-center gap-3 transition-all ${
                systemStatus === 'healthy'
                  ? 'border-[rgba(16,185,129,0.2)] bg-[rgba(16,185,129,0.04)]'
                  : systemStatus === 'warnings'
                  ? 'border-[rgba(245,158,11,0.2)] bg-[rgba(245,158,11,0.04)] cursor-pointer hover:border-[rgba(245,158,11,0.4)]'
                  : 'border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.04)] cursor-pointer hover:border-[rgba(239,68,68,0.4)]'
              }`}
              data-testid="system-status-overall"
            >
              {systemStatus === 'healthy' ? (
                <CheckCircle size={20} weight="fill" className="text-[#10B981] shrink-0" />
              ) : systemStatus === 'warnings' ? (
                <Warning size={20} weight="fill" className="text-[#F59E0B] shrink-0" />
              ) : (
                <WarningCircle size={20} weight="fill" className="text-[#EF4444] shrink-0" />
              )}
              <div>
                <p className="text-white text-sm font-semibold capitalize">{systemStatus === 'healthy' ? 'All Systems Healthy' : systemStatus === 'warnings' ? 'Needs Attention' : 'Issues Detected'}</p>
                <p className="text-[#8b8fa3] text-[10px]">{agents.length} agents · {runs.length} recent runs</p>
              </div>
            </div>

            {/* Pending approvals */}
            <div
              onClick={() => navigate('/approval-dashboard')}
              className={`rounded-xl border p-3 md:p-4 flex items-center gap-3 cursor-pointer transition-all hover:border-[rgba(255,255,255,0.15)] ${
                pendingApprovals > 0
                  ? 'border-[rgba(245,158,11,0.2)] bg-[rgba(245,158,11,0.04)]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)]'
              }`}
              data-testid="system-status-approvals"
            >
              <ShieldCheck size={20} weight="duotone" className={pendingApprovals > 0 ? 'text-[#F59E0B] shrink-0' : 'text-[#555] shrink-0'} />
              <div>
                <p className="text-white text-sm font-semibold">{pendingApprovals}</p>
                <p className="text-[#8b8fa3] text-[10px]">Pending Approvals</p>
              </div>
            </div>

            {/* Degraded agents */}
            <div
              onClick={() => degradedAgents.length > 0 ? navigate('/external-agent-health') : null}
              className={`rounded-xl border p-3 md:p-4 flex items-center gap-3 transition-all ${
                degradedAgents.length > 0
                  ? 'border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.04)] cursor-pointer hover:border-[rgba(239,68,68,0.4)]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)]'
              }`}
              data-testid="system-status-degraded"
            >
              <WarningCircle size={20} weight="duotone" className={degradedAgents.length > 0 ? 'text-[#EF4444] shrink-0' : 'text-[#555] shrink-0'} />
              <div>
                <p className="text-white text-sm font-semibold">{degradedAgents.length}</p>
                <p className="text-[#8b8fa3] text-[10px]">Degraded Agents</p>
              </div>
            </div>

            {/* Failed runs */}
            <div
              onClick={() => failedRuns.length > 0 ? navigate('/partner-pro/ai-workforce/run-history') : null}
              className={`rounded-xl border p-3 md:p-4 flex items-center gap-3 transition-all ${
                failedRuns.length > 0
                  ? 'border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.04)] cursor-pointer hover:border-[rgba(239,68,68,0.4)]'
                  : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)]'
              }`}
              data-testid="system-status-failed-runs"
            >
              <XCircle size={20} weight="duotone" className={failedRuns.length > 0 ? 'text-[#EF4444] shrink-0' : 'text-[#555] shrink-0'} />
              <div>
                <p className="text-white text-sm font-semibold">{failedRuns.length}</p>
                <p className="text-[#8b8fa3] text-[10px]">Failed Runs</p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ WHAT NEEDS ATTENTION ═══ */}
        {attentionItems.length > 0 && (
          <section data-testid="attention-panel">
            <h2 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
              <Warning size={16} className="text-[#F59E0B]" />
              Needs Attention
            </h2>
            <div className="space-y-2">
              {attentionItems.slice(0, 5).map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(255,255,255,0.12)] transition-all"
                    data-testid={`attention-item-${item.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon size={18} weight="fill" style={{ color: item.color }} className="shrink-0" />
                      <span className="text-sm text-white truncate">{item.title}</span>
                    </div>
                    <button
                      onClick={() => navigate(item.action.path)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[rgba(255,255,255,0.1)] text-[#8b8fa3] hover:text-white hover:border-[rgba(255,255,255,0.2)] transition-all shrink-0 ml-3"
                      data-testid={`attention-action-${item.id}`}
                    >
                      {item.action.label}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ═══ QUICK ACTIONS ═══ */}
        <section data-testid="dashboard-quick-actions">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {QUICK_ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.title}
                  data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => action.action === 'open_command' ? openCommand() : navigate(action.path)}
                  className="group text-left p-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(255,255,255,0.12)] hover:bg-[rgba(255,255,255,0.04)] transition-all duration-200"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${action.color}15` }}>
                    <Icon size={18} style={{ color: action.color }} />
                  </div>
                  <h3 className="text-white text-sm font-semibold mb-0.5 group-hover:text-[#EC4899] transition-colors">{action.title}</h3>
                  <p className="text-[#555] text-xs">{action.description}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* ═══ YOUR AGENTS ═══ */}
        <section data-testid="dashboard-your-agents">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <SquaresFour size={16} className="text-[#6366F1]" />
              Your Agents
            </h2>
            <Link to="/partner-pro/ai-workforce/agents" className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1">
              View all <CaretRight size={12} />
            </Link>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : agents.length === 0 ? (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-8 text-center" data-testid="dashboard-no-agents">
              <Lightning size={24} className="text-[#555] mx-auto mb-3" />
              <p className="text-[#8b8fa3] text-sm mb-1">No agents yet</p>
              <p className="text-[#555] text-xs mb-4">Install one from the gallery or build your own</p>
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => navigate('/automations')} className="text-xs px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#5558E6] text-white font-medium transition-colors">Browse Automations</button>
                <button onClick={() => openCommand()} className="text-xs px-4 py-2 rounded-lg border border-[rgba(255,255,255,0.1)] text-[#8b8fa3] hover:text-white transition-colors" data-testid="dashboard-build-agent-btn">Build Agent</button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {agents.map((agent) => {
                const opStatus = deriveOperationalStatus(agent);
                const needsAttention = opStatus === 'warning' || opStatus === 'error' || opStatus === 'degraded';
                return (
                  <div
                    key={agent.agent_id || agent.id}
                    className={`group rounded-xl border p-4 transition-all ${
                      needsAttention
                        ? 'border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.02)] hover:border-[rgba(239,68,68,0.35)]'
                        : 'border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(255,255,255,0.12)]'
                    }`}
                    data-testid={`agent-card-${agent.agent_id || agent.id}`}
                    style={{ transitionDuration: 'var(--motion-normal, 200ms)' }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AgentAvatar name={agent.name} size="sm" agentType={agent.agent_type || agent.agentType || agent.capability_profile} iconUrl={agent.icon_url} agent={agent} />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h3 className="text-white text-sm font-medium line-clamp-1">{agent.name}</h3>
                            <AgentStatusIndicator status={opStatus} variant="dot" size="xs" />
                          </div>
                          <AgentStatusIndicator status={opStatus} variant="minimal" size="xs" />
                        </div>
                      </div>
                    </div>
                    <p className="text-[#555] text-xs line-clamp-2 mb-2">{agent.description || agent.short_description || 'No description'}</p>
                    <OperationalMetrics
                      lastRunAt={agent.last_run_at || agent.updated_at}
                      runsToday={agent.runs_today || 0}
                      className="mb-3"
                    />
                    <button onClick={() => setAgentToRun(agent)} data-testid={`run-agent-dashboard-${agent.agent_id || agent.id}`} className="w-full text-xs py-1.5 rounded-lg border border-[rgba(99,102,241,0.3)] text-[#6366F1] hover:bg-[rgba(99,102,241,0.08)] transition-colors font-medium">Run Agent</button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ═══ RECENT ACTIVITY ═══ */}
        <section data-testid="dashboard-activity-feed">
          <ActivityFeed
            events={runs.map(run => ({
              id: run.run_id || run.id,
              type: run.status === 'completed' ? 'run_completed' : run.status === 'failed' ? 'run_failed' : run.status === 'running' ? 'run_started' : 'run_completed',
              agent_name: run.agent_name || run.agent_id || 'Agent',
              timestamp: run.created_at || run.updated_at,
              detail: run.status,
            }))}
            maxItems={10}
            loading={loading}
          />
        </section>

        {/* ═══ RECENT RUNS ═══ */}
        <section data-testid="dashboard-recent-runs">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Clock size={16} className="text-[#14B8A6]" />
              Recent Runs
            </h2>
            <Link to="/partner-pro/ai-workforce/run-history" className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1">
              View all <CaretRight size={12} />
            </Link>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-[#14B8A6] border-t-transparent rounded-full animate-spin" /></div>
          ) : runs.length === 0 ? (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-6 text-center" data-testid="dashboard-no-runs">
              <Clock size={20} className="text-[#555] mx-auto mb-2" />
              <p className="text-[#8b8fa3] text-sm">No runs yet</p>
              <p className="text-[#555] text-xs">Run an agent to see results here</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] overflow-hidden">
              {runs.map((run, idx) => (
                <div key={run.run_id || run.id || idx} data-testid={`recent-run-${run.run_id || run.id || idx}`} className={`flex items-center justify-between p-3 hover:bg-[rgba(255,255,255,0.02)] transition-colors cursor-pointer ${idx > 0 ? 'border-t border-[rgba(255,255,255,0.04)]' : ''}`} onClick={() => navigate('/partner-pro/ai-workforce/run-history')}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${run.status === 'completed' ? 'bg-green-400' : run.status === 'failed' ? 'bg-red-400' : run.status === 'running' ? 'bg-blue-400 animate-pulse' : 'bg-[#555]'}`} />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">{run.agent_name || run.agent_id || 'Agent Run'}</p>
                      <p className="text-[#555] text-[10px]">{run.status || 'unknown'} {run.created_at ? `· ${formatTimeAgo(run.created_at)}` : ''}</p>
                    </div>
                  </div>
                  <ArrowRight size={12} className="text-[#333] shrink-0" />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ═══ RECOMMENDED AUTOMATIONS ═══ */}
        <section data-testid="dashboard-recommended-automations">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Sparkle size={16} className="text-[#EC4899]" />
              Recommended Automations
            </h2>
            <Link to="/automations" className="text-xs text-[#8b8fa3] hover:text-[#EC4899] transition-colors flex items-center gap-1">
              View all <CaretRight size={12} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {automations.map((t) => {
              const color = OUTCOME_COLORS[t.outcome_category] || OUTCOME_COLORS['Operations'];
              const OutIcon = OUTCOME_ICONS[t.outcome_category] || GearSix;
              return (
                <Link key={t.slug} to={`/automations/${t.slug}`} data-testid={`recommended-automation-${t.slug}`} className="group block">
                  <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-4 hover:border-[rgba(255,255,255,0.12)] transition-all h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: color.bg }}>
                        <OutIcon size={12} style={{ color: color.text }} />
                      </div>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ backgroundColor: color.bg, color: color.text }}>{t.outcome_category}</span>
                    </div>
                    <h3 className="text-white text-sm font-medium mb-1 group-hover:text-[#EC4899] transition-colors line-clamp-1">{t.name}</h3>
                    <p className="text-[#555] text-xs line-clamp-2">{t.short_description || t.description?.replace(/\*\*/g, '').substring(0, 80)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ═══ PRO: AUTOMATION USAGE ═══ */}
        {isPro && usageStats && (
          <section data-testid="dashboard-automation-usage">
            <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <ChartBar size={16} className="text-[#6366F1]" />
              Automation Usage
            </h2>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-[rgba(99,102,241,0.15)] bg-[rgba(99,102,241,0.05)] p-4">
                <p className="text-[#8b8fa3] text-xs mb-1">Agents Deployed</p>
                <p className="text-2xl font-bold text-white">{usageStats.agentsDeployed}</p>
              </div>
              <div className="rounded-xl border border-[rgba(236,72,153,0.15)] bg-[rgba(236,72,153,0.05)] p-4">
                <p className="text-[#8b8fa3] text-xs mb-1">Total Agents</p>
                <p className="text-2xl font-bold text-white">{usageStats.totalAgents}</p>
              </div>
              <div className="rounded-xl border border-[rgba(20,241,149,0.15)] bg-[rgba(20,241,149,0.05)] p-4">
                <p className="text-[#8b8fa3] text-xs mb-1">Credits This Month</p>
                <p className="text-2xl font-bold text-white">{credits?.credit_balance?.toLocaleString() || 0}</p>
              </div>
            </div>
          </section>
        )}

        {/* ═══ PARTNER PRO: CLIENT ORGS ═══ */}
        {isPartnerPro && clientOrgs.length > 0 && (
          <section data-testid="dashboard-client-orgs">
            <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Buildings size={16} className="text-[#EC4899]" />
              Client Organisations
            </h2>
            <div className="rounded-xl border border-[rgba(255,255,255,0.08)] overflow-hidden">
              {clientOrgs.map((org, idx) => (
                <div key={org.organization_id || idx} className={`flex items-center justify-between p-3 ${idx > 0 ? 'border-t border-[rgba(255,255,255,0.04)]' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[rgba(236,72,153,0.12)] flex items-center justify-center">
                      <Buildings size={14} className="text-[#EC4899]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{org.name || org.organization_name || 'Organisation'}</p>
                      <p className="text-[#555] text-[10px]">{org.member_count || 0} members</p>
                    </div>
                  </div>
                  <CaretRight size={14} className="text-[#333]" />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Run Agent Modal */}
      {agentToRun && (
        <RunAgentModal
          agent={{ ...agentToRun, id: agentToRun.agent_id || agentToRun.id }}
          isOpen={!!agentToRun}
          onClose={() => setAgentToRun(null)}
          onSuccess={(sessionId) => { console.log('Session completed:', sessionId); }}
        />
      )}
    </>
  );
}
